<template>
    <div>
        <div class="row">
            <div class="col-lg-12 mb-3">
                <b-breadcrumb :items="breadcrumbItems" class="breadcrumb1 breadcrumbCustom border"/>
                <b-card :header="$t('general.super_admin.billings.title')" header-tag="h3" class="bg-default-card">
                    <data-table
                        v-if="tableLoaded"
                        :exportable = "false" title=""
                        :rows="getData.rowData"
                        :columns="columnData"
                        :perPage="perPage"
                        :rowsCount="rowsCount"
                        :currentPage="currentPage"
                        :pageCount="pageCount"
                        @rowClickDelete="onDeleteRow"
                        @rowClickEdit="onEditRow"
                        @rowClickPDF="onPDF"
                        @changePage="changePage"
                        @searchEvent="searchEvent"
                        @orderByEvent="orderByEvent"
                        class="shadow-none billing-table">
                    </data-table>

                    <b-btn class="btn btn-default mt-3" @click="onAddRow">
                        <i class="fa fa-plus-circle mr-1"></i> {{$t('general.super_admin.billings.create_billing')}}
                    </b-btn>
                </b-card>
            </div>
        </div>

        <b-modal :ok-title="$t('misc.ok')" :cancel-title="$t('misc.cancel')" ref="saveModal" :title="actionTitle" size="lg" @ok="saveAction" :ok-disabled="$store.state.ajax_in_progress || (row && !$store.getters.hasPermission(['platform'], ['update']))">
            <div class="row">
                <div class="col-sm-12">
                    <b-collapse v-model="showBillingEditForm" id="billingEditForm">
                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.invoice_no') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.noinvoice_no">
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.order_no') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.order_no">
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.date') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.date"></flat-pickr>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.expiration_date') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <flat-pickr :config="{enableTime: false, inline: false}" class="form-control bg-white" v-model="model.expiry_date"></flat-pickr>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.company') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedCompany"
                                    :options="currentCompany"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isCompanyLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="name" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onCompanySearch"
                                    @input="updateCompany"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedCompany != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedCompany"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.final_client') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <multiselect v-model="selectedFinalClient"
                                    :options="currentFinalClient"
                                    :multiple="false"
                                    :clear-on-select="true"
                                    :hide-selected="false"
                                    :loading="isFinalClientLoading"
                                    :preserve-search="true"
                                    placeholder=""
                                    label="name" track-by="id"
                                    :selectLabel="''"
                                    @search-change="onFinalClientSearch"
                                    @input="updateFinalClient"
                                >
                                    <template slot="clear" slot-scope="props">
                                        <div 
                                            v-if="selectedFinalClient != null" 
                                            class="multiselect__clear" 
                                            @mousedown.prevent.stop="clearSelectedFinalClient"
                                        >
                                        </div>
                                    </template>
                                    <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                                    <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                                </multiselect>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.currency') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <select v-model="model.currency" class="form-control" size="1">
                                    <option v-for="currency in currencies" :value="currency.code">{{currency.name}}</option>
                                </select>
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.discount') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.discount">
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>{{ $t('table.vat') }}</label>
                            </div>
                            <div class="col-lg-8" style="z-index: 0">
                                <toggle-button v-model="model.vat" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                            </div>
                        </div>

                        <div class="row mb-3" v-if="model.vat == 1">
                            <div class="col-lg-4">
                                <label>{{ $t('table.value') }}</label>
                            </div>
                            <div class="col-lg-8">
                                <input type="number" onKeyPress="if(this.value.length==8) return false;" step=".01" class="form-control" v-model="model.value">
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-lg-4">
                                <label>Status</label>
                            </div>
                            <div class="col-lg-8">
                                <select v-model="model.status" class="form-control" size="1">
                                    <option v-for="status in statuses" :value="status.code">{{status.name}}</option>
                                </select>
                            </div>
                        </div>
                    </b-collapse>

                    <b-collapse v-model="showItemEditForm" id="itemEditForm">
                        <billing-item-modal :item.sync="model.item"></billing-item-modal>
                        <div class="pull-right mb-2">
                            <button class="btn btn-success" @click="updateBillingItem">{{ $t('misc.save_item') }}</button>
                            <button class="btn btn-danger" @click="showItemEditForm = false;showBillingEditForm = true;">{{ $t('misc.cancel_edit') }}</button>
                        </div>
                    </b-collapse>

                    <div v-if="this.row">
                        <hr>
                        <h3>{{ $t('table.items') }}</h3>
                        <data-table
                            :exportable = "false" title=""
                            :rows="getData.itemData"
                            :columns="columnItemData"
                            :search="false"
                            :paginate="false"
                            @rowClickDelete="onDeleteItemRow"
                            @rowClickEdit="onEditItemRow"
                            class="shadow-none billing-table">
                        </data-table>

                        <b-btn class="btn btn-primary mt-3" @click="onAddItem">
                            <i class="fa fa-plus-circle mr-1"></i> {{$t('general.super_admin.billings.add_item')}}
                        </b-btn>
                    </div>
                </div>
            </div>
        </b-modal>

        <b-modal :ok-title="$t('misc.ok')" :cancel-title="$t('misc.cancel')" ref="itemModal" :title="actionTitle" size="lg" @hidden="onItemHidden" @ok="saveBillingItem()">
            <billing-item-modal :item.sync="model.item"></billing-item-modal>
        </b-modal>

        <confirm-dialog ref="deleteModal" :title="actionTitle" @buttonClick="deleteAction">
            {{ $t('messages.delete') }}
        </confirm-dialog>

        <confirm-dialog ref="deleteItem" :title="actionTitle" @buttonClick="deleteItemAction">
            {{ $t('messages.delete') }}
        </confirm-dialog>
    </div>
</template>

<script>
import Vue from 'vue';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import http from '@/libs/http';

import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

import * as notificationIcons from '@/components/global/miscellaneous/notificationIcons.js';
import miniToastr from 'mini-toastr';
import Multiselect from 'vue-multiselect';
import BillingItemModal from '@/components/global/modals/BillingItemModal.vue';

import {minitstr_config} from '@/constants';
miniToastr.init(minitstr_config);
import _ from 'lodash';

export default {
    name: 'billing',
    components: {
        Multiselect,
        DataTable,
        ConfirmDialog,
        notificationIcons,
        flatPickr,
        BillingItemModal
    },
    data() {
        return {
            breadcrumbItems: [
                {
                    text: Vue.i18n.translate('general.dashboard.title'),
                    to: '/',
                },
                {
                    text: Vue.i18n.translate('general.super_admin.billings.title'),
                    active: true
                }
            ],
            tableName: 'billing',
            tableNameTranslated: Vue.i18n.translate('general.super_admin.billings.title_modal'),
            row: '',
            selectedItem: '',
            tableLoaded: false,
            perPage: 0,
            rowsCount: 0,
            pageCount: 1,
            currentPage: 1,
            sortType: 'asc',
            sortBy: 'id',
            searchInput: '',
            actionTitle: '',
            
            showItemEditForm: false,
            showBillingEditForm: true,
            
            selectedCompany: null,
            currentCompany: [],
            isCompanyLoading: false,

            selectedFinalClient: null,
            currentFinalClient: [],
            isFinalClientLoading: false,

            getData: {
                rowData: [],
                itemData: []
            },
            model: {
                noinvoice_no: null,
                order_no: null,
                date: null,
                expiry_date: null,
                company_id: null,
                company_client_id: null,
                currency: null,
                discount: null,
                vat: null,
                value: null,
                status: null,
                item: {
                    name: null,
                    cost: null,
                    vat: null,
                    quantity: null,
                    description: null,
                    haveVat: false
                }
            },
            columnData: [
                {
                    label: Vue.i18n.translate('misc.actions'),
                    field: '',
                    width: '130px',
                    showDeleteButton: true,
                    showEditButton: true,
                    showPDF: true,
                },
                {
                    label: Vue.i18n.translate('table.invoice_no'),
                    field: 'noinvoice_no',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.date'),
                    field: 'date',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('table.status'),
                    field: 'statusV',
                    numeric: false,
                    html: false,
                }
            ],

            columnItemData: [
                {
                    label: Vue.i18n.translate('misc.actions'),
                    field: '',
                    width: '90px',
                    showDeleteButton: true,
                    showEditButton: true
                },
                {
                    label: Vue.i18n.translate('general.super_admin.billings.items.name'),
                    field: 'name',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('general.super_admin.billings.items.cost'),
                    field: 'cost',
                    numeric: false,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('general.super_admin.billings.items.vat'),
                    field: 'vat',
                    numeric: true,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('general.super_admin.billings.items.quantity'),
                    field: 'quantity',
                    numeric: true,
                    html: false,
                },
                {
                    label: Vue.i18n.translate('general.super_admin.billings.items.description'),
                    field: 'description',
                    numeric: false,
                    html: false,
                }
            ]
        }
    },
    mounted () {
        this.onLoadTable();
    },
    computed: {
        currencies: {
            get: function () {
                let currencies = this.$store.getters.getCurrencies;
                return currencies;
            }
        },
        statuses: {
            get: function () {
                let statuses = this.$store.getters.getBillingStatuses;
                return statuses;
            }
        },
        vats: {
            get: function () {
                let vats = this.$store.getters.getBillingVat;
                return vats;
            }
        },
    },
    watch: {
        currencies: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        },
        statuses: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        },
        vats: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        }
    },
    methods: {
        successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
        orderByEvent (column, sort_type) {
            this.sortType = sort_type;
            this.sortBy = column.field;
            this.onLoadTable();
        },
        searchEvent (searchInput) {
            this.searchInput = searchInput;
            this.onLoadTable();
        },
        changePage (page) {
            this.currentPage = page;
            this.onLoadTable();
        },
        onLoadTable () {
            let data = http.route(this.tableName + '?search=' + this.searchInput + '&page=' + this.currentPage + '&sort_by=' + this.sortBy + '&sort_order=' + this.sortType).get();
            data.then(response => {
                if (!response.isError) {
                    this.perPage     = response.pagination.limit;
                    this.rowsCount   = response.pagination.totalEntries;
                    this.currentPage = response.pagination.currentPage;
                    this.pageCount   = response.pagination.totalPages;
                    var data = response.data;
                    data.forEach(field => {
                        if (typeof this.statuses[field.status] != 'undefined') {
                            field.statusV = this.statuses[field.status].name;
                        }
                    });
                    this.getData.rowData = data;
                    this.tableLoaded = true;
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        onPDF (row) {
           let data = http.route('billing/' + row.id).get(
            {
                export_pdf: 1
            }, true);
            data.then(response => {
                if (!response.isError) {
                    let data = http.route('get_content/'+response.data.type+'/'+response.data.id+'/'+response.data.name).downloadPdf({}, response.data.name).then(res => {
                        if (typeof res == 'undefined') {
                            miniToastr.success(Vue.i18n.translate('messages.pdf_generated'), Vue.i18n.translate('messages.success'));
                        } else {
                            Object.keys(res.errorMessage).forEach(key => {
                                this.errorMsg(Vue.i18n.translate(res.errorMessage[key]));
                            });
                        }
                    });
                } else {
                    miniToastr.error(Vue.i18n.translate('errors.error_generating_pdf'), Vue.i18n.translate('messages.error'));
                }
            });
        },
        clearSearches () {
            this.selectedCompany  = null;
            this.currentCompany   = [];
            this.isCompanyLoading = false;

            this.selectedFinalClient  = null;
            this.currentFinalClient   = [];
            this.isFinalClientLoading = false;
        },
        onAddRow () {
            this.actionTitle = Vue.i18n.translate('modal.general.create') + ' ' + this.tableNameTranslated;
            this.row = null;
            this.selectedItem = null;
            this.model.noinvoice_no = null;
            this.model.order_no = null;
            this.model.date = null;
            this.model.expiry_date = null;
            this.model.company_id = null;
            this.model.company_client_id = null;
            this.model.currency = null;
            this.model.discount = null;
            this.model.vat = null;
            this.model.value = null;
            this.model.status = null;
            this.clearSearches();
            this.$refs.saveModal.show();
        },
        onEditRow (row) {
            this.actionTitle = Vue.i18n.translate('modal.general.edit') + ' ' + this.tableNameTranslated;
            this.row = row;
            this.model.noinvoice_no = row.noinvoice_no;
            this.model.order_no = row.order_no;
            this.model.date = row.date;
            this.model.expiry_date = row.expiry_date;
            this.model.company_id = row.company_id;
            this.model.company_client_id = row.company_client_id;
            this.model.currency = row.currency;
            this.model.discount = row.discount;
            this.model.vat = Boolean(row.vat);
            this.model.value = row.value;
            this.model.status = row.status;
            this.selectedCompany = row.company;
            this.selectedFinalClient = row.client_company;

            this.selectedItem = null;

            this.showBillingEditForm = true;
            this.showItemEditForm = false;

            this.loadItems(row.id);

            this.$refs.saveModal.show();
        },
        onDeleteRow (row, index) {
            this.actionTitle = Vue.i18n.translate('modal.general.delete') + ' ' + this.tableNameTranslated;
            this.row = row
            this.$refs.deleteModal.show();
        },
        onDeleteItemRow (row, index) {
            this.actionTitle = Vue.i18n.translate('modal.general.delete') + ' ' + Vue.i18n.translate('general.super_admin.billings.items.title');
            this.selectedItem = row;
            this.$refs.deleteItem.show();
        },
        onEditItemRow(row, index) {
            this.selectedItem = row;
            this.model.item.name = row.name;
            this.model.item.cost = row.cost;
            this.model.item.vat = row.vat;
            this.model.item.quantity = row.quantity;
            this.model.item.description = row.description;
            this.model.item.haveVat = (this.row.vat == "yes");

            this.showItemEditForm = true;
            this.showBillingEditForm = false;
        },
        saveAction (e) {
            let put_data = {
                noinvoice_no: this.model.noinvoice_no,
                order_no: this.model.order_no,
                date: this.model.date,
                expiry_date: this.model.expiry_date,
                company_id: this.model.company_id,
                company_client_id: this.model.company_client_id,
                currency: this.model.currency,
                discount: this.model.discount,
                vat: this.model.vat,
                value: this.model.value,
                status: this.model.status,
            }

            Object.keys(put_data).forEach(key => {
				if (put_data[key] === '') {
					put_data[key] = null;
				}
			});

            if (this.row) {
                let data = http.route(this.tableName + '/' + this.row.id).patch(put_data, true);
                data.then(response => {
                    if (response.isError == false) {
                        this.onLoadTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                    } else {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    }
                });
            } else {
                let data = http.route(this.tableName).post(put_data, true);
                data.then(response => {
                    if (response.isError == false) {
                        this.onLoadTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.created'));
                    } else {
                        Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
                    }
                });
            }
        },
        saveBillingItem() {
            let put_data = {
                billing_id: this.row.id,
                name: this.model.item.name,
                cost: this.model.item.cost,
                vat: this.model.item.vat,
                quantity: this.model.item.quantity,
                description: this.model.item.description,
            };

            Object.keys(put_data).forEach(key => {
				if (put_data[key] === '') {
					put_data[key] = null;
				}
			});

            let data = http.route('billingitem').post(put_data, true);
            data.then(response => {
                if (response.isError == false) {
                    this.onEditRow(this.row);
                    this.successMsg(Vue.i18n.translate('messages.billing_item_created'));
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        updateBillingItem() {
            let put_data = {
                name: this.model.item.name,
                cost: this.model.item.cost,
                vat: this.model.item.vat,
                quantity: this.model.item.quantity,
                description: this.model.item.description,
            };

            Object.keys(put_data).forEach(key => {
				if (put_data[key] === '') {
					put_data[key] = null;
				}
			});

            let data = http.route("billingitem/"+this.selectedItem.id).patch(put_data, true);
            data.then(response => {
                if (response.isError == false) {
                    this.showItemEditForm = false;
                    this.onEditRow(this.row);
                    this.successMsg(Vue.i18n.translate('messages.billing_item_updated'));
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        deleteAction (button) {
            if (button == 1) {
                let data = http.route(this.tableName + '/' + this.row.id).delete();
                data.then(response => {
                    if (response.isError == false) {
                        if (this.currentPage > 1 && this.getData.rowData.length == 1) {
                            this.currentPage = this.currentPage - 1;
                        }
                        
                        this.onLoadTable();
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.deleted'));
                    } else {
                        this.errorMsg(Vue.i18n.translate('errors.error_delete'));
                    }
                });
            }    
            this.$refs.deleteModal.hide();
        },
        deleteItemAction (button) {
            if (button == 1) {
                let data = http.route('billingitem/' + this.selectedItem.id).delete();
                data.then(response => {
                });
            }

            this.showItemEditForm = false;
            this.showBillingEditForm = true;
            this.onEditRow(this.row);
        },
        onAddItem() {
            this.actionTitle = Vue.i18n.translate('modal.general.create') + ' ' + Vue.i18n.translate('general.super_admin.billings.items.title');
            this.model.item.name = null;
            this.model.item.cost = null;
            this.model.item.vat = null;
            this.model.item.quantity = null;
            this.model.item.description = null;
            this.model.item.haveVat = Boolean(this.row.vat);
            this.selectedItem = null;
            this.$refs.saveModal.hide();
            this.$refs.itemModal.show();
        },
        onItemHidden() {
            this.$refs.saveModal.show();
        },
        loadItems(billing_id) {
            let data = http.route('billingitem').get();
            data.then(response => {
                if (response.isError == false) {
                    this.getData.itemData = response.data;
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        onCompanySearch(search) {
            if (search == '') {
                this.currentCompany = [];
                
                return false;
            }

            this.isCompanyLoading = true;
            this.searchCompany(search, this);
        },
        searchCompany: _.debounce((search, vm) => {
            let data = http.route('company?get_all=1&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentCompany = response.data;
                }
                vm.isCompanyLoading = false;
            });
        }, 500),
        updateCompany(company) {
            this.model.company_id = (company != null) ? company.id : null;
        },
        clearSelectedCompany () {
            this.selectedCompany  = null;
            this.model.company_id = null;
        },
        onFinalClientSearch(search) {
            if (search == '') {
                this.currentFinalClient = [];
                
                return false;
            }

            this.isFinalClientLoading = true;
            this.searchFinalClient(search, this);
        },
        searchFinalClient: _.debounce((search, vm) => {
            let data = http.route('company?get_all=1&search_select=' + search).get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentFinalClient = response.data;
                }
                vm.isFinalClientLoading = false;
            });
        }, 500),
        updateFinalClient(company_client) {
            this.model.company_client_id = (company_client != null) ? company_client.id : null;
        },
        clearSelectedFinalClient () {
            this.selectedFinalClient     = null;
            this.model.company_client_id = null;
        },
    }
}

</script>

<style scoped>
	select {
		-webkit-appearance:menulist-button;
		-moz-appearance:menulist-button;
	}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>



// WEBPACK FOOTER //
// src/components/pages/superadmin/billings.vue